import React, { useEffect, useState, useCallback, useMemo, useRef } from "react";
import { Wheel } from "../components/Wheel";
import GameShell from "../components/GameShell";
import ClaimFlow from "../components/ClaimFlow";
import PlayLimitNotice from "../components/PlayLimitNotice";
import { api } from "../lib/api";
import { getDeviceId } from "../lib/device";
import { decorateSegments } from "../lib/prizeAssets";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";

const LOGO = "/assets/brand/logo.png";
const POINTER = "/assets/roue/pointer.png";
const TOPPER = "/assets/roue/smiley.png";

export default function WheelPage() {
  const [config, setConfig] = useState(null);
  const [spinning, setSpinning] = useState(false);
  const [canPlay, setCanPlay] = useState(true);
  const [spinResult, setSpinResult] = useState(null);
  const deviceId = getDeviceId();
  const wheelRef = useRef(null);
  const pendingRef = useRef(null);

  const segments = useMemo(() => decorateSegments(config?.segments), [config]);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/config/roue");
      setConfig(data);
      const cp = await api.get(`/can-play/${encodeURIComponent(deviceId)}?game=roue`);
      setCanPlay(cp.data.can_play);
    } catch (e) {
      toast.error("Impossible de charger la roue");
    }
  }, [deviceId]);

  useEffect(() => { load(); }, [load]);

  const handleSpin = async () => {
    if (spinning || !config) return;
    if (!canPlay) { toast("🌙 Un seul jeu par jour — reviens demain !"); return; }
    setSpinning(true);
    setSpinResult(null);
    try {
      const { data } = await api.post("/spin", { device_id: deviceId });
      pendingRef.current = data;
      wheelRef.current?.spinTo(data.segment_index);
    } catch (e) {
      setSpinning(false);
      toast.error(e?.response?.data?.detail || "Erreur lors du tour");
      if (e?.response?.status === 429) setCanPlay(false);
    }
  };

  const handleRest = () => {
    const data = pendingRef.current;
    setSpinning(false);
    setCanPlay(false);
    if (data) setSpinResult(data);
  };

  return (
    <GameShell title={config?.title || "La Roue Gourmande"} subtitle={config?.subtitle || "Tente ta chance !"}>
      <div className="relative flex items-center justify-center my-2">
        {config ? (
          <Wheel
            ref={wheelRef}
            segments={segments}
            size={330}
            pointerSrc={POINTER}
            topperSrc={TOPPER}
            hubLogoSrc={LOGO}
            onRest={handleRest}
          />
        ) : (
          <Loader2 className="animate-spin text-white" size={48} />
        )}
      </div>

      <button
        data-testid="spin-button"
        onClick={handleSpin}
        disabled={spinning || !canPlay}
        className="yum-cta mt-8"
      >
        {spinning ? (
          <span className="flex items-center gap-2"><Loader2 className="animate-spin" size={22} /> Ça tourne…</span>
        ) : !canPlay ? "Déjà joué" : "Tente ta chance 🎡"}
      </button>

      {!canPlay && !spinning && !spinResult && <PlayLimitNotice />}

      {canPlay && !spinResult && (
        <div className="bg-white text-ink rounded-3xl p-4 mt-6 max-w-sm w-full text-left space-y-3" style={{ boxShadow: "0 8px 0 #a81a0c" }}>
          {["Clique pour faire tourner la roue", "Découvre ton cadeau", "Régale-toi chez YÜM !"].map((txt, i) => (
            <p key={i} className="flex items-center gap-3 font-semibold">
              <span className="flex-none w-7 h-7 rounded-full bg-brand text-white grid place-items-center font-display font-bold">{i + 1}</span>
              {txt}
            </p>
          ))}
        </div>
      )}

      <ClaimFlow result={spinResult} onClose={() => setSpinResult(null)} />
    </GameShell>
  );
}
